import { useState } from 'react';
import { Filter, Plus, Power, Save, Sparkles, Trash2 } from 'lucide-react';
import { parseKeywords, type KeywordBatchView } from '@nlinks/shared';
import { ApiError } from '@/lib/api';
import { useKeywordBatchMutations, useKeywordBatches } from '@/lib/queries';
import { formatNumber } from '@/lib/format';
import { InfoButton } from '@/components/InfoButton';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Modal,
  Spinner,
  Toggle,
  useToast,
} from '@/components/ui';

const BATCH_INFO =
  'A saved set of keywords, named after the dates it ran. Several can be active at once — a message is kept if it matches any of them.';

function BatchRow({
  batch,
  onToggle,
  onDelete,
  busy,
}: {
  batch: KeywordBatchView;
  onToggle: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? batch.keywords : batch.keywords.slice(0, 12);

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-200">{batch.label}</span>
            <Badge tone={batch.isActive ? 'success' : 'neutral'}>{batch.isActive ? 'Active' : 'Stopped'}</Badge>
            <span className="text-xs text-slate-600">
              {batch.keywords.length} keyword{batch.keywords.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {shown.map((keyword) => (
              <Badge key={keyword} tone="brand">
                {keyword}
              </Badge>
            ))}
            {batch.keywords.length > shown.length ? (
              <button
                onClick={() => setExpanded(true)}
                className="text-xs text-brand-400 transition hover:text-brand-300"
              >
                +{batch.keywords.length - shown.length} more
              </button>
            ) : null}
          </div>
        </div>

        <dl className="flex shrink-0 gap-4 text-right text-xs">
          <div>
            <dt className="text-slate-600">Matched</dt>
            <dd className="tabular-nums text-brand-300">{formatNumber(batch.totals.messagesMatched)}</dd>
          </div>
          <div>
            <dt className="text-slate-600">Links</dt>
            <dd className="tabular-nums text-slate-300">{formatNumber(batch.totals.linksFound)}</dd>
          </div>
          <div>
            <dt className="text-slate-600">Opened</dt>
            <dd className="tabular-nums text-emerald-400">{formatNumber(batch.totals.mailsOpened)}</dd>
          </div>
        </dl>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggle}
            loading={busy}
            icon={<Power className="h-3.5 w-3.5" />}
          >
            {batch.isActive ? 'Stop' : 'Start'}
          </Button>
          <button
            onClick={onDelete}
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-500/10 hover:text-red-400"
            aria-label={`Delete the ${batch.label} batch`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </li>
  );
}

/** The add/edit form, reused by the keywords page and the onboarding wizard. */
export function KeywordBatchForm({
  hasExistingActive,
  onSaved,
  submitLabel = 'Save keywords',
}: {
  hasExistingActive: boolean;
  onSaved?: () => void;
  submitLabel?: string;
}) {
  const { save } = useKeywordBatchMutations();
  const { push } = useToast();

  const [raw, setRaw] = useState('');
  const [deactivateExisting, setDeactivateExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Previewed with the same parser the server uses, so what is shown here
  // cannot disagree with what gets saved.
  const keywords = parseKeywords(raw);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await save.mutateAsync({ raw, deactivateExisting });
      setRaw('');
      setDeactivateExisting(false);
      push(
        'success',
        `Saved ${result.keywords.length} keyword${result.keywords.length === 1 ? '' : 's'}. ` +
          (result.mailboxesQueued > 0
            ? `Checking ${result.mailboxesQueued} mailbox(es) now.`
            : 'Add a mailbox to start checking.'),
      );
      onSaved?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save these keywords.');
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      {error ? <Alert severity="error">{error}</Alert> : null}

      <textarea
        className="input-base min-h-[140px] resize-y font-mono text-sm"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder={'"amazon" OR "flipkart" OR "black friday"\n\nor: amazon, flipkart, "black friday"'}
        maxLength={8000}
      />

      {keywords.length > 0 ? (
        <div>
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {keywords.length} keyword{keywords.length === 1 ? '' : 's'} will be matched
          </p>
          <div className="flex flex-wrap gap-1.5">
            {keywords.slice(0, 40).map((keyword) => (
              <Badge key={keyword} tone="brand">
                {keyword}
              </Badge>
            ))}
            {keywords.length > 40 ? (
              <span className="text-xs text-slate-500">+{keywords.length - 40} more</span>
            ) : null}
          </div>
        </div>
      ) : raw.trim().length > 0 ? (
        <Alert severity="warning">
          No usable keywords found. Separate terms with commas or <code>OR</code>.
        </Alert>
      ) : null}

      {hasExistingActive ? (
        <div className="rounded-lg border border-surface-border bg-surface-base/50 p-4">
          <Toggle
            checked={deactivateExisting}
            onChange={setDeactivateExisting}
            label="Stop the batches currently running"
            description="Leave off to run this alongside them — mail matching any active batch is kept. Turn on if this replaces them."
          />
        </div>
      ) : null}

      <div className="flex justify-end border-t border-surface-border pt-5">
        <Button type="submit" loading={save.isPending} disabled={keywords.length === 0} icon={<Save className="h-4 w-4" />}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export function KeywordsPage() {
  const { data: batches, isLoading } = useKeywordBatches();
  const { setActive, remove } = useKeywordBatchMutations();
  const { push } = useToast();

  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<KeywordBatchView | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const activeCount = batches?.filter((b) => b.isActive).length ?? 0;

  const toggle = async (batch: KeywordBatchView) => {
    setBusyId(batch.id);
    try {
      await setActive.mutateAsync({ id: batch.id, isActive: !batch.isActive });
      push('success', batch.isActive ? `${batch.label} stopped.` : `${batch.label} started.`);
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not change that batch.');
    } finally {
      setBusyId(null);
    }
  };

  const destroy = async () => {
    if (!confirmDelete) return;
    try {
      const result = await remove.mutateAsync(confirmDelete.id);
      push(
        'success',
        `${confirmDelete.label} deleted. ${formatNumber(result.untaggedMessages)} message(s) kept.`,
      );
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not delete that batch.');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-slate-100">
            Keywords
            <InfoButton label="Keyword batches">{BATCH_INFO}</InfoButton>
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Only mail matching an active batch is kept and has its links opened.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} icon={<Plus className="h-4 w-4" />}>
          Add keywords
        </Button>
      </div>

      {!isLoading && activeCount === 0 ? (
        <Alert severity="error" title="Monitoring is paused">
          With no active batch nothing is checked at all: no mail is fetched and no links are opened. This is
          deliberate — the app will not open mail you have not asked it to. Save a batch below, or reactivate one,
          and every mailbox is re-checked straight away.
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Your batches"
          description={
            batches?.length
              ? `${activeCount} active of ${batches.length}`
              : 'Named after the dates they ran'
          }
        />
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : batches?.length ? (
          <ul className="divide-y divide-surface-border">
            {batches.map((batch) => (
              <BatchRow
                key={batch.id}
                batch={batch}
                busy={busyId === batch.id}
                onToggle={() => void toggle(batch)}
                onDelete={() => setConfirmDelete(batch)}
              />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Filter className="h-6 w-6" />}
            title="No keywords yet"
            description="Nothing is monitored until you add one. Your mailboxes stay connected and idle."
            action={
              <Button onClick={() => setAdding(true)} icon={<Plus className="h-4 w-4" />}>
                Add your first batch
              </Button>
            }
          />
        )}
      </Card>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add keywords"
        description="Separate terms with commas or OR. Use “quotes” to keep a phrase together."
        size="lg"
      >
        <KeywordBatchForm hasExistingActive={activeCount > 0} onSaved={() => setAdding(false)} />
      </Modal>

      <Modal
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete this batch?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={destroy} loading={remove.isPending}>
              Delete batch
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          <span className="font-medium text-slate-200">{confirmDelete?.label}</span> will stop matching and disappear
          from the batch filter. The{' '}
          <span className="font-medium text-slate-200">
            {formatNumber(confirmDelete?.totals.messagesMatched ?? 0)}
          </span>{' '}
          message(s) it already found are kept — your daily totals stay accurate.
        </p>
      </Modal>
    </div>
  );
}
